import React, { RefAttributes, useContext } from "react";
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import Account from "./Account";
import CLI from "./CLI";
import APIKeys from "./APIKeys";
import { useIdentityFeatureFlagEnabled } from "utils/posthogFeatureFlags";
import {
  CommandLineIcon,
  KeyIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";
import SettingsScopeToggle from "components/ApplicationShell/SettingsScopeToggle";
import { OrgContext } from "contexts/OrgContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import {
  applicationShellPageContentClassName,
  applicationShellPageLayoutClassName,
} from "components/ApplicationShell/pageSpacing";

const SettingsLink = ({
  icon,
  path,
  label,
}: {
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  path: string;
  label: string;
}) => {
  const IconComponent = icon;
  const match = useRouteMatch(path);
  return (
    <div
      className={`text-sm ${
        match ? "font-bold text-highlight" : "text-gray-900 dark:text-white"
      }`}
    >
      <Link to={path} className="flex flex-row gap-2 items-center">
        <IconComponent className="w-4 h-4" />
        {label}
      </Link>
    </div>
  );
};

const AccountSettings = () => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const allowAPIKeys = useIdentityFeatureFlagEnabled("allow-api-keys");
  const orgContext = useContext(OrgContext);
  const shellPreference = useApplicationShellPreference();
  const showSettingsTabs = isApplicationShellRedesigned(shellPreference);

  const activeTab = location.pathname.startsWith(`${path}/cli`)
    ? "cli"
    : allowAPIKeys && location.pathname.startsWith(`${path}/api-keys`)
      ? "api-keys"
      : "account";
  const selectTab = (tab: string) => {
    history.push(`${path}/${tab}`);
  };

  const settingsRoutes = (
    <Switch>
      <Redirect exact from="/settings" to="/settings/account" />
      <Route exact path={`${path}/account`} component={Account} />
      {allowAPIKeys ? (
        <Route exact path={`${path}/api-keys`} component={APIKeys} />
      ) : (
        <Redirect exact from={`${path}/api-keys`} to={`${path}/account`} />
      )}
      <Route exact path={`${path}/cli`} component={CLI} />
    </Switch>
  );

  const pageContent = (
    <>
      <div
        className={
          showSettingsTabs
            ? "flex flex-col gap-4 pb-8 sm:flex-row sm:items-start sm:justify-start"
            : "flex flex-col gap-4 pb-8 pt-10 sm:flex-row sm:items-start sm:justify-start"
        }
      >
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {showSettingsTabs ? "Account Settings" : "Settings"}
        </h1>
        <SettingsScopeToggle
          activeScope="personal"
          organizationId={orgContext.activeOrgId}
          personalLabel={showSettingsTabs ? "Personal" : "Account"}
        />
      </div>
      {showSettingsTabs ? (
        <Tabs value={activeTab} onValueChange={selectTab}>
          <TabsList className="mb-6 overflow-x-auto">
            <TabsTrigger value="account">Account</TabsTrigger>
            {allowAPIKeys && (
              <TabsTrigger value="api-keys">API Keys</TabsTrigger>
            )}
            <TabsTrigger value="cli">CLI</TabsTrigger>
          </TabsList>
          <TabsContent value="account">{settingsRoutes}</TabsContent>
          {allowAPIKeys && (
            <TabsContent value="api-keys">{settingsRoutes}</TabsContent>
          )}
          <TabsContent value="cli">{settingsRoutes}</TabsContent>
        </Tabs>
      ) : (
        <div className="flex flex-col gap-6 pb-5 sm:flex-row sm:gap-2 sm:py-5">
          <div className="flex flex-col gap-3 w-40">
            <SettingsLink
              icon={UserIcon}
              path={`${path}/account`}
              label="Account"
            />
            {allowAPIKeys && (
              <SettingsLink
                icon={KeyIcon}
                path={`${path}/api-keys`}
                label="API Keys"
              />
            )}
            <SettingsLink
              icon={CommandLineIcon}
              path={`${path}/cli`}
              label="CLI"
            />
          </div>
          <div className="flex-1">{settingsRoutes}</div>
        </div>
      )}
    </>
  );

  return (
    <ApplicationPageLayout
      className={
        showSettingsTabs
          ? applicationShellPageLayoutClassName
          : "px-4 pb-8 sm:px-6 sm:pb-16 lg:px-8"
      }
    >
      {showSettingsTabs ? (
        <div className={applicationShellPageContentClassName}>
          {pageContent}
        </div>
      ) : (
        pageContent
      )}
    </ApplicationPageLayout>
  );
};

export default AccountSettings;
